import React, { useState } from 'react';
import { Formik } from 'formik';
import { TextField, Button,Grid2, Typography, Box, Autocomplete, Checkbox, FormControlLabel, Paper, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CounselingDetailsFormValues, validationSchema, initialValues, CounselingType, CounselingFeedback, getSrcOfReference, SourceOfReference, UploadedFile, CounselingDetailsPostPayload, CounselingConfigResponse, getCounselingConfig, CounsellingDetailsURL, deleteCounselingAttachment } from './counselingDetailsFormHelper';
import { RequiredLabel } from '../../../../../utils/commonUtils';
import CounselingDetailsViewMode from './Counseling-details-ViewMode';
import { useLocation, useNavigate } from 'react-router-dom';
import { DoctorListResponse, getDrList, PackageVisit } from '../../package.helper';
import { useAppQuery } from '../../../../../services/appQuery';
import { getRequest, postRequest, putRequest } from '../../../../../services/httpService';
import { useAlert } from '../../../../../components/alertProvider';
import { ConfirmationModal } from '../../../../../components/confirmationModal';
import { tomorrowISO, todayISO } from '../../../../../utils/dateUtils';
import { useCounselingStore } from '../../../store';
import { UploadButton, UploadedImageType, ImageUploadModal, FileViewerModal, extractFilename } from '../../../../../components/common/ImageUploadModal';
import { useLoader } from '../../../../../components/loaderProvider/loader';

const CounselingDetails: React.FC = () => {
  // Get counseling session ID from Zustand store (set when clicking on package row)
  const storedCounselingSessionId = useCounselingStore((state) => state.counselingSessionId);
  const setCounselingSessionId = useCounselingStore((state) => state.setCounselingSessionId);
  const counselingSessionId = React.useMemo(() => {
    // Prefer packageVisit value, fallback to store value
    return storedCounselingSessionId ?? null;
  }, [storedCounselingSessionId]);
  const [finalCouselingSessionId, setFinalCouselingSessionId] = React.useState<number | null>(counselingSessionId);

  const hasValidSessionId =
    typeof finalCouselingSessionId === 'number' &&
    Number.isFinite(finalCouselingSessionId) &&
    finalCouselingSessionId > 0;

  // Initialize isViewMode to true if we have a valid session ID (editing existing data)
  // This prevents flash of edit form before view form loads
  const [isViewMode, setIsViewMode] = React.useState(hasValidSessionId);
  const { t : tBilling} = useTranslation('billing_config');
  const { t : tGeneral} = useTranslation('general');
  const { t : tPackage} = useTranslation('Packagebilling');
  const { addAlert } = useAlert();
  const { showLoader, hideLoader } = useLoader();
  const [openModal1, setOpenModal1] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const packageVisit: PackageVisit | undefined = (location.state as { packageVisit?: PackageVisit })?.packageVisit;
  
  // Images per counseling type - keyed by type name (Treatment, Psychological, Financial)
  const [imgAttachmentsPerType, setImgAttachmentsPerType] = useState<Record<string, UploadedImageType[]>>({});
  // Images for API payload per type (cleaned base64 without prefix)
  const [uploadedImagesPerType, setUploadedImagesPerType] = useState<Record<string, { image?: string; file_name?: string; file_size?: number; file_extension?: string; }[]>>({});
  // Track which counseling type is currently active for upload modal
  const [activeTypeIndex, setActiveTypeIndex] = useState<number | null>(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  
  // Get type key from index (using Formik values passed as param)
  const getTypeKey = (counselingTypes: CounselingType[], idx: number): string => {
    const type = counselingTypes[idx];
    return type?.type?.name || type?.type_label || `type_${idx}`;
  };
  
  const openUploadModal = (idx: number): void => {
    setActiveTypeIndex(idx);
    setIsModalOpen(true);
  };
  
  const closeUploadModal = (): void => {
    setIsModalOpen(false);
    setActiveTypeIndex(null);
  };
  
  const openViewerModal = (idx: number): void => {
    setActiveTypeIndex(idx);
    setIsViewerOpen(true);
  };
  
  const closeViewerModal = (): void => {
    setIsViewerOpen(false);
    setActiveTypeIndex(null);
  };

  // Handle saving images from upload modal for a specific counseling type
  const handleSaveImages = (images: UploadedImageType[], typeKey: string) => {
    // Update attachments for this specific type (create deep copy)
    setImgAttachmentsPerType(prev => ({
      ...prev,
      [typeKey]: [...images],
    }));
    
    // Process images for API payload:
    // Only send NEW images (base64) - existing images are already on the server
    const cleanedImages: { 
      image?: string;
      file_name?: string;
      file_size?: number;
      file_extension?: string;
    }[] = [];
    
    images.forEach((img) => {
      const imageData = img.image;
      
      // Check if this is an existing image (S3 URL) - skip it, backend already has it
      const isUrl = imageData.startsWith('http://') || imageData.startsWith('https://');
      
      if (img.isExisting || isUrl) {
        // Skip existing images - they're already on the server
        return;
      }
      
      // New image - send full base64 string (with data:xxx;base64, prefix)
      cleanedImages.push({ 
        image: imageData,
        file_name: img.name,
        file_size: img.sizeBytes,
        file_extension: img.file_extension,
      });
    });

    // Store cleaned images for the save payload for this specific type
    setUploadedImagesPerType(prev => ({
      ...prev,
      [typeKey]: cleanedImages,
    }));
    addAlert(tGeneral('Images saved successfully.'), 'success');
    closeUploadModal();
  };

  // Handler for removing images in the modal for a specific type
  const handleRemoveImages = (images: UploadedImageType[], typeKey: string) => {
    setImgAttachmentsPerType(prev => ({
      ...prev,
      [typeKey]: [...images],
    }));
  };

  // Handler for deleting an existing image from the API
  const deleteImage = async (imageId: string | number): Promise<void> => {
    const numericId = typeof imageId === 'number' ? imageId : parseInt(imageId, 10);
    
    if (isNaN(numericId)) {
      // eslint-disable-next-line no-console
      console.error('Invalid image ID:', imageId);
      addAlert(tGeneral('Failed to delete image'), 'error');
      return;
    }
    
    // eslint-disable-next-line no-console
    console.log('Deleting image with ID:', numericId);
    showLoader();
    try {
      await deleteCounselingAttachment(numericId);
      addAlert(tGeneral('Image deleted successfully'), 'success');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error deleting image:', err);
      addAlert(tGeneral('Failed to delete image'), 'error');
      throw err; // Re-throw so ImageUploadModal knows deletion failed
    } finally {
      hideLoader();
    }
  };

  // Counselor name from localStorage user object (first_name + last_name)
  const counselorNameFromLocalStorage = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const user = JSON.parse(raw);
      const first = user?.first_name ?? '';
      const last = user?.last_name ?? '';
      const full = `${first} ${last}`.trim();
      return full || null;
    } catch {
      return null;
    }
  }, []);
    // Fetch existing counseling details in edit mode using session id from packageVisit
  interface CounselingDetailsGetResponse {
    session_details: CounselingDetailsFormValues['session_details'];
    counseling_types: CounselingType[];
    feedback: CounselingFeedback[];
    images?: { image?: string; id?: number; file_name?: string; file_size?: number; file_extension?: string }[];
  }
  // Mutation via useAppQuery (query wrapper) with guard to avoid double alerts
  const [submitPayload, setSubmitPayload] = React.useState<CounselingDetailsPostPayload | null>(null);
  const [submissionId, setSubmissionId] = React.useState(0); // increments per submit
  const [pendingValues, setPendingValues] = React.useState<CounselingDetailsFormValues | null>(null);
  const lastHandledIdRef = React.useRef<number | null>(null);



  const { data: existingDetails, refetch: refetchExistingDetails, isFetched: isExistingDetailsFetched, isError: isExistingDetailsError } =
    useAppQuery<CounselingDetailsGetResponse>(
      ['counselingDetails', hasValidSessionId ? finalCouselingSessionId : null],
      () => {
        if (!hasValidSessionId) {
          return Promise.resolve(
            null as unknown as CounselingDetailsGetResponse
          );
        }

        return getRequest<CounselingDetailsGetResponse>(
          `${CounsellingDetailsURL()}${finalCouselingSessionId}/`
        );
      },
      {
        enabled: hasValidSessionId || !!finalCouselingSessionId,
        staleTime: 0,
        retry: false,
        gcTime: 0,
      }
    );

  const {
    data: postData,
    isError: postIsError,
    error: postError,
    isSuccess: postIsSuccess,
    isFetching: isSavingCounseling,
  } = useAppQuery<CounselingDetailsGetResponse | null>(
    ['postCounselingDetails', submissionId],
    () => {
      if (!submitPayload && !finalCouselingSessionId) return Promise.resolve(null);
 
      if (existingDetails && finalCouselingSessionId) {
        // Use PUT for update
        return putRequest<CounselingDetailsGetResponse>(`${CounsellingDetailsURL()}${finalCouselingSessionId}/`, submitPayload);
      }
      // Use POST for add
      return postRequest<CounselingDetailsGetResponse>(CounsellingDetailsURL(), submitPayload);
    },
    {
      enabled: !!submitPayload,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  // Helper to get current time in HH:MM format
  const getCurrentTime = (): string => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Patch form initial values from packageVisit if available
  const patchedInitialValues = React.useMemo(() => {
    const currentTime = getCurrentTime();
    // If edit mode and existing details loaded, use them
    if (existingDetails) {
      // Merge existing counseling_types into the 3 base types; enable only those with data
      const baseTypes = initialValues.counseling_types;
      const existingList = existingDetails.counseling_types ?? [];
      const mergedTypes: CounselingType[] = baseTypes.map(base => {
        const match = existingList.find(et => {
          const baseId = base.type?.id ?? null;
          const etId = et.type?.id ?? null;
          const baseName = base.type?.name ?? base.type_label ?? '';
          const etName = et.type?.name ?? et.type_label ?? '';
          return (baseId && etId && baseId === etId) || (!!baseName && !!etName && baseName === etName);
        });
        if (!match) {
          return { ...base, enabled: false, date: '', remark: '', report_file: null };
        }
        return {
          ...base,
          ...match,
          enabled: !!(match.date || match.remark || match.report_file),
        };
      });

      const mapped: CounselingDetailsFormValues = {
        session_details: {
          ...initialValues.session_details,
          ...existingDetails.session_details,
          // Override specific fields from packageVisit as requested
          treatment_advice: packageVisit?.treatment_advice_id ?? initialValues.session_details.treatment_advice,
          source_of_reference: packageVisit?.source_of_reference ?? initialValues.session_details.source_of_reference,
          reference_details: packageVisit?.additional_referral_details ?? initialValues.session_details.reference_details,
        },
        counseling_types: mergedTypes,
        feedback: existingDetails.feedback ?? initialValues.feedback,
      };

      return mapped;
    }
    // Default: patch from packageVisit if available
    if (!packageVisit) return {
      ...initialValues,
      session_details: {
        ...initialValues.session_details,
        counseling_time: currentTime,
      },
    };
    return {
      ...initialValues,
      session_details: {
        ...initialValues.session_details,
        patient: packageVisit.patient ?? initialValues.session_details.patient,
        treatment_doctor: packageVisit.treating_doctor_id ?? initialValues.session_details.treatment_doctor,
        source_of_reference: packageVisit.source_of_reference ?? initialValues.session_details.source_of_reference,
        reference_details: packageVisit.additional_referral_details ?? initialValues.session_details.reference_details,
        treatment_advice: packageVisit.treatment_advice_id ?? initialValues.session_details.treatment_advice,
        counselor_name: (packageVisit.counselor_name ?? counselorNameFromLocalStorage ?? initialValues.session_details.counselor_name),
        counseling_time: currentTime,
      },
    };
  }, [existingDetails, packageVisit, counselorNameFromLocalStorage]);

  // Fetch doctor list for treatment doctor autocomplete
  const { data: doctorList, isPending: doctorListPending } = useAppQuery<DoctorListResponse>(
    ['doctorList'],
    () => getRequest<DoctorListResponse>(getDrList()),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: counselingConfig } = useAppQuery<CounselingConfigResponse>(
    ['counselingConfig'],
    () => getRequest<CounselingConfigResponse>(getCounselingConfig()),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: srcOfReferenceOptions=[], isPending: srcOfReferenceLoading  } = useAppQuery<SourceOfReference[]>(
    ['srcOfReferenceOptions'],
    () => getRequest<SourceOfReference[]>(getSrcOfReference()),
    { staleTime: 5 * 60 * 1000 }
  );




  const buildPayload = (values: CounselingDetailsFormValues) => {
    const session = values.session_details;
    const counseling_types = values.counseling_types
      .filter(ct => ct.enabled && ct.date)
      .map(ct => {
        const typeKey = ct.type?.name || ct.type_label || '';
        const typeImages = uploadedImagesPerType[typeKey] || [];
        return {
          ...(ct.id ? { id: ct.id } : {}),
          type: (ct.type?.name ?? null),
          date: ct.date,
          remark: ct.remark,
          report_file: typeImages, // Include images for this specific counseling type
        };
      });
    const feedback = values.feedback
      .filter(f => f.feedback_date && f.patient_response)
      .map(f => ({
        ...(f.id ? { id: f.id } : {}),
        feedback_date: f.feedback_date,
        patient_response: f.patient_response,
        patient_remarks: f.patient_remarks,
        feedback: f.feedback,
      }));
    return {
      session_details: {
        ...(session.id ? { id: session.id } : {}),

        patient: session.patient,
        counseling_date: session?.counseling_date,
        counseling_time: session?.counseling_time,
        counselor_name: session?.counselor_name,
        next_visit_date: session?.next_visit_date,
        counseling_stage: session?.counseling_stage,
        counseling_remark: session?.counseling_remark,
        source_of_reference: session?.source_of_reference,
        reference_details: session?.reference_details,
        treatment_advice: session?.treatment_advice,
        treatment_doctor: session?.treatment_doctor,
        is_freezed: session?.is_freezed,
      },
      counseling_types,
      feedback,
    };
  };



  React.useEffect(() => {
    // Only handle once per submissionId
    if (submitPayload && lastHandledIdRef.current !== submissionId) {
      const pd = postData as Record<string, unknown> | null;
      const businessSuccess = pd ? ('success' in pd ? Boolean(pd.success as boolean) : true) : false;
      if (postIsSuccess && businessSuccess) {
        addAlert(tPackage('package_messages.counseling_save_success', 'Counseling details saved successfully.'), 'success');
        lastHandledIdRef.current = submissionId;
        setSubmitPayload(null); // reset so query won't rerun
        setCounselingSessionId(postData?.session_details?.id ?? null);
        setFinalCouselingSessionId(postData?.session_details?.id ?? null);
        refetchExistingDetails();
      } else if (postIsError || (postIsSuccess && !businessSuccess)) {
        addAlert(tPackage('package_messages.counseling_save_failed', 'Failed to save counseling details.'), 'error');
        // eslint-disable-next-line no-console
        console.error('Post counseling details error:', postError ?? postData);
        lastHandledIdRef.current = submissionId;
        setSubmitPayload(null);
      }
    }
  }, [postIsSuccess, postIsError, postError, postData, submitPayload, submissionId, addAlert, navigate, refetchExistingDetails, tPackage, setCounselingSessionId]);

  const submitCounselingDetails = (values: CounselingDetailsFormValues) => {
    const payload: CounselingDetailsPostPayload = buildPayload(values);
    // eslint-disable-next-line no-console
    console.log('Submitting Counseling Details Payload:', payload);

    setSubmitPayload(payload);
    setSubmissionId(id => id + 1);
  };
  


  
  const handleCloseModal1 = () => { setOpenModal1(false); };
  
  React.useEffect(() => {
    if (existingDetails) {
      setIsViewMode(true);
    } else if (isExistingDetailsFetched && !existingDetails) {
      // No data exists (404 or empty response), switch to edit mode
      setIsViewMode(false);
    } else if (isExistingDetailsError) {
      // Error fetching data, switch to edit mode
      setIsViewMode(false);
    }
  }, [existingDetails, isExistingDetailsFetched, isExistingDetailsError]);

  // Load existing images per counseling type when editing
  React.useEffect(() => {
    if (existingDetails?.counseling_types && existingDetails.counseling_types.length > 0) {
      const imagesPerType: Record<string, UploadedImageType[]> = {};
      
      existingDetails.counseling_types.forEach((ct) => {
        const typeKey = ct.type?.name || ct.type_label || '';
        // Check if report_file is an array of images
        const reportFiles = Array.isArray(ct.report_file) ? ct.report_file : [];
        if (reportFiles.length > 0) {
          // eslint-disable-next-line no-console
          console.log(`Loading existing images for type ${typeKey}:`, reportFiles);
          const existingImages: UploadedImageType[] = reportFiles.map((img, index) => {
            const imageUrl = img.image || '';
            const imageId = img.id;
            
            // Priority: 1) file_name from API, 2) Extract from URL, 3) Default name
            const backendFileName = img.file_name;
            const extractedName = extractFilename(imageUrl || '');
            const cleanFileName = backendFileName || extractedName || `Image_${index + 1}`;
            
            return {
              name: cleanFileName,
              image: imageUrl,
              size: img.file_size ? (img.file_size / (1024 * 1024)).toFixed(2) : undefined,
              sizeBytes: img.file_size,
              file_extension: img.file_extension,
              isExisting: true, // Mark as existing image from API
              id: imageId, // Store original ID
            };
          });
          imagesPerType[typeKey] = existingImages;
        }
      });
      
      // eslint-disable-next-line no-console
      console.log('Processed existing images per type:', imagesPerType);
      setImgAttachmentsPerType(imagesPerType);
    }
  }, [existingDetails]);


  if (isViewMode) {
    if (!existingDetails) return null;
    return (
      <CounselingDetailsViewMode
        data={existingDetails}
        onBack={() => setIsViewMode(false)}
      />
    );
  }
  return (
    <Formik
      initialValues={patchedInitialValues}
      validationSchema={validationSchema}
      onSubmit={(vals) => { setPendingValues(vals); setOpenModal1(true); }}
      enableReinitialize
    >
      {({
        values,
        setFieldValue,
        handleBlur,
        handleChange,
        handleSubmit,
        errors,
        touched,
      }) => {

        // Handlers for Formik-managed fields
        const handleFeedbackChange = (idx: number, field: keyof CounselingFeedback, value: string | number) => {
          const updatedRows = values.feedback.map((row, i) => i === idx ? { ...row, [field]: value } : row);
          setFieldValue('feedback', updatedRows);
        };

        const handleCounselingTypeChange = (
          idx: number,
          field: keyof CounselingType,
          value: string | number | boolean | UploadedFile | null
        ) => {
          const updatedTypes = values.counseling_types.map((type, i) =>
            i === idx ? { ...type, [field]: value } : type
          );
          setFieldValue('counseling_types', updatedTypes);
        };
        // Toggle enabled state and clear dependent fields in a single update to avoid race conditions
        const toggleCounselingTypeEnabled = (idx: number, checked: boolean) => {
          const updatedTypes = values.counseling_types.map((type, i) => {
            if (i !== idx) return type;
            if (checked) {
              return { ...type, enabled: true };
            }
            return { ...type, enabled: false, date: '', remark: '', report_file: null };
          });
          setFieldValue('counseling_types', updatedTypes);
        };
        // Error helpers
        const ctErr = (i: number, field: keyof CounselingType) => {
          const t = Array.isArray(touched.counseling_types)
            ? (touched.counseling_types[i] as import('formik').FormikTouched<CounselingType>)
            : undefined;
          const e = Array.isArray(errors.counseling_types)
            ? (errors.counseling_types[i] as import('formik').FormikErrors<CounselingType>)
            : undefined;
          return !!(t && e && (e as Record<string, unknown>)[field]);
        };
        const fbErr = (i: number, field: keyof CounselingFeedback) => {
          const t = Array.isArray(touched.feedback)
            ? (touched.feedback[i] as import('formik').FormikTouched<CounselingFeedback>)
            : undefined;
          const e = Array.isArray(errors.feedback)
            ? (errors.feedback[i] as import('formik').FormikErrors<CounselingFeedback>)
            : undefined;
          return !!(t && e && (e as Record<string, unknown>)[field]);
        };



        const now = new Date();

        // Check if all rows are older than 24 hours
        const allOlderThan24 = values.feedback.every(row => {
          if (!row.updated_on) return false;  // if any row has no updated_on, stop
          const updatedTime = new Date(row.updated_on);
          const diffHours = (now.getTime() - updatedTime.getTime()) / (1000 * 60 * 60);
          return diffHours > 24;
        });

        if (allOlderThan24 && !values.session_details.is_freezed) {
          // Prepend a blank row to appear first
          setFieldValue('feedback', [
            {
              feedback_date: '',
              patient_response: '',
              patient_remarks: '',
              feedback: '',
              updated_on: ''   // keep blank; will fill when user edits
            },
            ...values.feedback
          ]);
        }

        return (
          <Box data-testid="counseling-details-container" sx={{ px: 3 }}>
            <form data-testid="counseling-details-form" onSubmit={handleSubmit} autoComplete="off">
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {tBilling('counseling_details.counseling_details')}
              </Typography>

              <Grid2 container spacing={2}>
                {/* Session Details Fields */}
                <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                  <TextField
                    fullWidth
                    size='small'
                    label={RequiredLabel(tBilling('counseling_details.counseling_date'), true)}
                    name="session_details.counseling_date"
                    type="date"
                    value={values.session_details?.counseling_date}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.session_details?.counseling_date && Boolean(errors?.session_details?.counseling_date)}
                  />
                </Grid2>
                <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                  <TextField
                    fullWidth
                    size='small'
                    label={RequiredLabel(tBilling('counseling_details.counseling_time'), true)}
                    name="session_details.counseling_time"
                    type="time"
                    value={values.session_details.counseling_time}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.session_details?.counseling_time && Boolean(errors.session_details?.counseling_time)}
                  />
                </Grid2>
                <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                  <TextField
                    fullWidth
                    size='small'
                    label={RequiredLabel(tBilling('counseling_details.counselor_name'), true)}
                    name="session_details.counselor_name"
                    value={values.session_details.counselor_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled
                    sx={{
                      backgroundColor: '#fafafa',
                      opacity: 0.9,
                      transition: 'background-color 0.3s ease, opacity 0.3s ease',
                    }}
                    error={touched.session_details?.counselor_name && Boolean(errors.session_details?.counselor_name)}
                  />
                </Grid2>
                <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                  <TextField
                    fullWidth
                    size='small'
                    label={RequiredLabel(tBilling('counseling_details.next_visit_date_by_counsellor'), false)}
                    name="session_details.next_visit_date"
                    type="date"
                    slotProps={{ inputLabel: { shrink: true }, input: { inputProps: { min: (values.session_details.next_visit_date && values.session_details.next_visit_date < tomorrowISO()) ? undefined : tomorrowISO() } } }}
                    value={values.session_details.next_visit_date ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldValue('session_details.next_visit_date', value === '' ? null : value);
                    }}
                    onBlur={handleBlur}
                  />
                </Grid2>
                <Grid2  size={{xl:3, lg:4, md:6, xs:12}}>
                  <Autocomplete
                    fullWidth
                    size='small'
                    options={counselingConfig?.COUNSELING_STAGE ?? []}
                    getOptionLabel={(option) => option.name}
                    value={counselingConfig?.COUNSELING_STAGE?.find(opt => opt.code === values.session_details.counseling_stage) || null}
                    onChange={(_e, newValue) => {
                      setFieldValue('session_details.counseling_stage', newValue ? newValue.code : '');
                    }}
                    onBlur={handleBlur}
                    
                    
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={RequiredLabel(tBilling('counseling_details.counseling_stage'), true)}
                        error={touched.session_details?.counseling_stage && Boolean(errors.session_details?.counseling_stage)}
                        
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                  <TextField
                    fullWidth
                    size='small'
                    label={RequiredLabel(tBilling('counseling_details.counseling_remark'), false)}
                    name="session_details.counseling_remark"
                    value={values.session_details.counseling_remark}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.session_details?.counseling_remark && Boolean(errors.session_details?.counseling_remark)}
                  />
                </Grid2>
                <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                  <Autocomplete
                    fullWidth
                    size='small'
                    options={srcOfReferenceOptions ?? []}
                    getOptionLabel={option => option.name}
                    disabled
                    sx={
                      {backgroundColor: '#fafafa',
                        opacity: 0.9,
                        transition: 'background-color 0.3s ease, opacity 0.3s ease',}
                  
                    }
                    loading={srcOfReferenceLoading}
                    value={srcOfReferenceOptions?.find(opt => opt.code === values.session_details.source_of_reference) || null}
                    onChange={(_e, newValue) => {
                      setFieldValue('session_details.source_of_reference', newValue ? newValue.code : '');
                    }}
                    onBlur={handleBlur}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={RequiredLabel(tBilling('counseling_details.source_of_reference'), false)}
                        
                        error={touched.session_details?.source_of_reference && Boolean(errors.session_details?.source_of_reference)}
                        sx={values.session_details.is_freezed ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                  <TextField
                    fullWidth
                    size='small'
                    label={RequiredLabel(tBilling('counseling_details.reference_doctor'), false)}
                    name="session_details.reference_details"
                    disabled
                    sx={{
                      backgroundColor:  '#fafafa ', 
                      opacity: 0.9 ,
                      transition: 'background-color 0.3s ease, opacity 0.3s ease', 
                    }}
                    value={values.session_details.reference_details}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.session_details?.reference_details && Boolean(errors.session_details?.reference_details)}
                  />
                </Grid2>
                <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                  <Autocomplete
                    fullWidth
                    size='small'
                    disabled
                    sx={{
                      backgroundColor:  '#fafafa ', 
                      opacity: 0.9 ,
                      transition: 'background-color 0.3s ease, opacity 0.3s ease', 
                    }}
                    options={packageVisit ? [{ label: packageVisit.treatment_advice, id: packageVisit.treatment_advice_id }] : []}
                    getOptionLabel={option => (option?.label ?? '').toString()}
                    value={packageVisit ? { label: packageVisit.treatment_advice, id: packageVisit.treatment_advice_id } : null}
                    onChange={(_e, newValue) => {
                      setFieldValue('session_details.treatment_advice', newValue ? newValue.id : '');
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={RequiredLabel(tBilling('counseling_details.treatment_advice'), false)}
                        error={touched.session_details?.treatment_advice && Boolean(errors.session_details?.treatment_advice)}
                        
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                  <Autocomplete
                    fullWidth
                    size='small'
                    options={doctorList?.objects ?? []}
                    getOptionLabel={option => `${option.first_name} ${option.last_name}`.trim()}
                    loading={doctorListPending}
                    value={doctorList?.objects?.find(doc => doc.id === values.session_details.treatment_doctor) || null}
                    onChange={(_e, newValue) => {
                      setFieldValue('session_details.treatment_doctor', newValue ? newValue.id : '');
                    }}
                    onBlur={handleBlur}
                    
                    
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={RequiredLabel(tBilling('counseling_details.treatment_doctor'), true)}
                        error={touched.session_details?.treatment_doctor && Boolean(errors.session_details?.treatment_doctor)}

                      />
                    )}
                  />
                </Grid2>

              </Grid2>

              <Box>
                <Divider sx={{ my: 3, borderColor: '#afb0b1ff' }} />
              </Box>
              {/* Counseling Types Section */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {tBilling('counseling_details.counseling_type')}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 4 }}>
                  {values.counseling_types.map((type, idx) => (
                    <Grid2 container alignItems="center" spacing={2} key={idx} sx={{ mb: 2 }}>
                      <Grid2 size={{xl:2, lg:3, md:4, xs:12}}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!type.enabled}
                              onChange={e => {
                                toggleCounselingTypeEnabled(idx, e.target.checked);
                              }}
                              color="primary"
                            />
                          }
                          label={<Typography sx={{ fontWeight: 500 }}>{type.type_label ? tBilling(`counseling_details.${type.type_label.toLowerCase()}`) : tBilling(`counseling_details.${type.type?.name.toLowerCase()}`)}</Typography>}
                        />
                      </Grid2>
                      <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                        <TextField
                          sx={!type.enabled ? {
                            backgroundColor: '#fafafa',
                            opacity: 0.9,
                            transition: 'background-color 0.3s ease, opacity 0.3s ease',
                          } : undefined}
                          fullWidth
                          size="small"
                          type="date"
                          label={tBilling('counseling_details.date')}
                          value={type.date}
                          onChange={e => handleCounselingTypeChange(idx, 'date', e.target.value)}
                          disabled={!type.enabled }
                          slotProps={{ inputLabel: { shrink: true }, input: { inputProps: { min: (type.date && type.date < todayISO()) ? undefined : todayISO() } } }}
                          error={ctErr(idx, 'date')}
                        />
                      </Grid2>
                      <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                        <TextField
                          fullWidth
                          sx={!type.enabled ? {
                            backgroundColor: '#fafafa',
                            opacity: 0.9,
                            transition: 'background-color 0.3s ease, opacity 0.3s ease',
                          } : undefined}
                          size="small"
                          label={tBilling('counseling_details.remark')}
                          value={type.remark}
                          onChange={e => handleCounselingTypeChange(idx, 'remark', e.target.value)}
                          disabled={!type.enabled }
                          error={ctErr(idx, 'remark')}
                          slotProps={{ inputLabel: { shrink: !!type.remark || undefined } }}
                        />
                      </Grid2>
                      <Grid2 size={{xl:4, lg:4, md:6, xs:12}}>
                        <UploadButton
                          disabled={!type.enabled}
                          label={tGeneral('Upload Image')}
                          onClick={() => openUploadModal(idx)}
                          onViewClick={() => openViewerModal(idx)}
                          fileCount={(imgAttachmentsPerType[getTypeKey(values.counseling_types, idx)] || []).length}
                          variant="outlined"
                          size="medium"
                          uploadTooltip={tGeneral('Upload new images')}
                          viewTooltip={tGeneral('View uploaded images')}
                        />
                      </Grid2>
                    </Grid2>
                  ))}
                </Paper>
              </Box>
              <Box>
                <Divider sx={{ my: 3, borderColor: '#afb0b1ff' }} />
              </Box>
              {/* Feedback Section */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {tBilling('counseling_details.feedback_details')}
                </Typography>

                {values.feedback.map((row, idx) => (
                  <Grid2 container spacing={2} key={idx} sx={{ mb: 2 }}>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      {/** determine disabled state: freezed or older than 24h */}
                      {(() => {
                        const updatedOn = row.updated_on as unknown as string | undefined;
                        const isOlderThan24 = (() => {
                          if (!updatedOn) return false;
                          const updatedTime = new Date(updatedOn);
                          const diffHours = (Date.now() - updatedTime.getTime()) / (1000 * 60 * 60);
                          return diffHours > 24;
                        })();
                        const isRowDisabled = Boolean(isOlderThan24);

                        return (
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            disabled={isRowDisabled}
                            label={RequiredLabel(tBilling('counseling_details.advice_date'), true)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            value={row.feedback_date }
                            onChange={e => handleFeedbackChange(idx, 'feedback_date', e.target.value)}
                            error={fbErr(idx, 'feedback_date')}
                          />
                        );
                      })()}
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      {(() => {
                        const updatedOn = row.updated_on as unknown as string | undefined;
                        const isOlderThan24 = (() => {
                          if (!updatedOn) return false;
                          const updatedTime = new Date(updatedOn);
                          const diffHours = (Date.now() - updatedTime.getTime()) / (1000 * 60 * 60);
                          return diffHours > 24;
                        })();
                        const isRowDisabled = isOlderThan24;

                        return (
                          <Autocomplete
                            fullWidth
                            size="small"
                            disabled={isRowDisabled}
                            options={counselingConfig?.PATIENT_RESPONSE ?? []}
                            getOptionLabel={option => option.name}
                            value={counselingConfig?.PATIENT_RESPONSE?.find(opt => opt.code === row.patient_response) || null}
                            onChange={(_e, newValue) => handleFeedbackChange(idx, 'patient_response', newValue ? newValue.code : '')}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label={RequiredLabel(tBilling('counseling_details.patient_response'), true)}
                                error={fbErr(idx, 'patient_response')}
                              />
                            )}
                          />
                        );
                      })()}
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      {(() => {
                        const updatedOn = row.updated_on as unknown as string | undefined;
                        const isOlderThan24 = (() => {
                          if (!updatedOn) return false;
                          const updatedTime = new Date(updatedOn);
                          const diffHours = (Date.now() - updatedTime.getTime()) / (1000 * 60 * 60);
                          return diffHours > 24;
                        })();
                        const isRowDisabled = isOlderThan24;
                        
                        return (
                          <TextField
                            fullWidth
                            disabled={isRowDisabled}
                            size="small"
                            label={tBilling('counseling_details.patient_remarks')}
                            value={row.patient_remarks || ''}
                            onChange={e => handleFeedbackChange(idx, 'patient_remarks', e.target.value)}
                            placeholder={tBilling('Advance.type_here', { defaultValue: 'Type Here' })}
                            error={fbErr(idx, 'patient_remarks')}
                          />
                        );
                      })()}
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      {(() => {
                        const updatedOn = row.updated_on as unknown as string | undefined;
                        const isOlderThan24 = (() => {
                          if (!updatedOn) return false;
                          const updatedTime = new Date(updatedOn);
                          const diffHours = (Date.now() - updatedTime.getTime()) / (1000 * 60 * 60);
                          return diffHours > 24;
                        })();
                        const isRowDisabled =  isOlderThan24;

                        return (
                          <TextField
                            fullWidth
                            size="small"
                            disabled={isRowDisabled}
                            label={tBilling('counseling_details.feedback')}
                            value={row.feedback || ''}
                            onChange={e => handleFeedbackChange(idx, 'feedback', e.target.value)}
                            placeholder={tBilling('Advance.type_here', { defaultValue: 'Type Here' })}
                            error={fbErr(idx, 'feedback')}
                          />
                        );
                      })()}
                    </Grid2>
                  </Grid2>
                ))}



                <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, justifyContent: 'end' }}>

                  {/* <FormControlLabel
                    control={
                      <Checkbox
                        name="session_details.is_freezed"
                        checked={values.session_details.is_freezed}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label={RequiredLabel('Is Freezed', false)}
                  /> */}

                  <Box >
                    <Button variant="outlined" sx={{ mr: 2, minWidth: 120, fontWeight: 600, borderRadius: 2 }} onClick={() => navigate(-1)} disabled={isSavingCounseling}>
                      {tBilling('counseling_details.back_to_listing')}
                    </Button>
                    {/* <Button
                      type="button"
                      variant="contained"
                      sx={{mr: 2, minWidth: 120, fontWeight: 600, borderRadius: 2, backgroundColor: '#444', '&:hover': { backgroundColor: '#222' } }}
                      onClick={() => {
                        document.querySelector('form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                        const hasErrors = Object.keys(errors).length > 0;
                        if (!hasErrors) {
                          setFieldValue('session_details.is_freezed', true);
                        }
                      }}
                    >
                      {tBilling('counseling_details.save_and_finalize')}
                    </Button> */}
                    <Button type="submit" variant="contained" sx={{ minWidth: 120, fontWeight: 600, borderRadius: 2, backgroundColor: isSavingCounseling ? '#9e9e9e' : '#444', '&:hover': { backgroundColor: isSavingCounseling ? '#9e9e9e' : '#222' } }} disabled={isSavingCounseling}>
                      {tBilling('counseling_details.save')}
                    </Button>
                  </Box>
                </Box>
              </Box>

              <ConfirmationModal
                open={openModal1}
                handleClose={handleCloseModal1}
                onConfirm={() => { if (pendingValues) submitCounselingDetails(pendingValues); }}
                type={'save'}
                title={'Counseling Details'}
                contentMessage={'Are you sure you want to save counseling details ?'}
                isConfirming={isSavingCounseling}
              />

              {/* Image Upload Modal */}
              <ImageUploadModal 
                open={isModalOpen} 
                onClose={closeUploadModal} 
                onSave={(images) => {
                  const typeKey = activeTypeIndex !== null 
                    ? getTypeKey(values.counseling_types, activeTypeIndex) 
                    : '';
                  handleSaveImages(images, typeKey);
                }}  
                uploadedImages={
                  activeTypeIndex !== null 
                    ? (imgAttachmentsPerType[getTypeKey(values.counseling_types, activeTypeIndex)] || [])
                    : []
                } 
                onRemove={(images) => {
                  const typeKey = activeTypeIndex !== null 
                    ? getTypeKey(values.counseling_types, activeTypeIndex) 
                    : '';
                  handleRemoveImages(images, typeKey);
                }}
                onDeleteImage={(imageId: number) => deleteImage(imageId)}
              />

              {/* File Viewer Modal */}
              <FileViewerModal
                open={isViewerOpen}
                onClose={closeViewerModal}
                files={
                  activeTypeIndex !== null 
                    ? (imgAttachmentsPerType[getTypeKey(values.counseling_types, activeTypeIndex)] || [])
                    : []
                }
                title={tGeneral('Uploaded Files')}
              />
            </form>
          </Box>
        );
      }}
    </Formik>
  );
};

export default CounselingDetails;
